<template>
  <span class="subscription-address-update-button">
    <SloTooltip
      :enabled="disabled"
      :text="'Subscription order is being processed and changes to this order can no longer be made.' | t"
      position="top-right"
    >
      <button
        :disabled="disabled"
        @click="modalOpen = true"
        class="p-0 bg-transparent fw-book c-blue-400 hover:c-blue-700"
      >
        {{ 'Update shipping' | t }}
      </button>
    </SloTooltip>
    <transition name="fade-up">
      <SloModal v-if="modalOpen" :maxWidth="500" :form="false" @dismiss="handleCloseModal">
        <template v-slot:title>
          {{ 'Update shipping address' | t }}
        </template>
        <SloLoader :loading="shippingAddressesLoading" size="xl" class="mt-md">
          <ShippingAddresses
            :show-remove-address="false"
            :show-modify-controls="false"
            :shipping-addresses="shippingAddresses"
            :shipping-address-id="shippingAddressId"
            @selectAddress="handleAddressSelect"
          />
          <router-link
            :to="{
              name: 'marketplace-account-address-add',
              query: {subscription_redirect: $route.fullPath}
            }"
            class="mt-lg block"
          >
            {{ 'Add a new address' | t }}
          </router-link>
        </SloLoader>
        <template v-slot:footer>
          <div class="grid grid-cols-1 gap-md w-100 sm:grid-cols-2">
            <SloButton @click="handleCloseModal" :disabled="actionButtonsDisabled" fill="outline">
              {{ 'Discard Changes' | t }}
            </SloButton>
            <SloButton @click="handleShippingAddressUpdate" :disabled="actionButtonsDisabled">
              <SloLoader :loading="shippingAddressesUpdateLoading" size="md" color="white">
                {{ 'Update' | t }}
              </SloLoader>
            </SloButton>
          </div>
        </template>
      </SloModal>
    </transition>
  </span>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import SloModal from '../../../lib/components/SloModal.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import ShippingAddresses from './ShippingAddresses.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloTooltip from '../../../lib/components/SloTooltip.vue';

export default {
  name: 'SubscriptionShippingAddressUpdateButton',
  components: {
    SloLoader,
    SloButton,
    ShippingAddresses,
    SloIcon,
    SloModal,
    SloTooltip
  },
  filters: {
    t
  },
  props: {
    disabled: {type: Boolean, default: false},
    subscriptionId: {type: String|Number, required: true},
    currentShippingAddressId: {type: String|Number, required: true}
  },
  data() {
    return {
      modalOpen: false,
      shippingAddressesLoading: true,
      shippingAddressesUpdateLoading: false,
      shippingAddressId: null,
      shippingAddresses: []
    };
  },
  computed: {
    actionButtonsDisabled() {
      return this.shippingAddressesLoading || this.shippingAddressesUpdateLoading;
    }
  },
  watch: {
    async modalOpen(isOpen) {
      if (isOpen) {
        this.shippingAddressId = this.currentShippingAddressId;

        await marketplaceApi.getCurrentUserSubscriptionAddresses(this.subscriptionId)
          .then(async res => {
            this.shippingAddresses = res.data.addresses;
            this.shippingAddressesLoading = false;

            if (this.shippingAddresses.length === 0) {
              await this.$router.push({name: 'marketplace-account-address-add'});
            }
          })
          .catch(async() => {
            this.$snotify.error(t('Error loading your addresses, please try again'));
            await this.$router.push({name: 'marketplace-account-subscriptions'});
          });
      }
    }
  },
  methods: {
    async handleShippingAddressUpdate() {
      try {
        this.shippingAddressesUpdateLoading = true;
        await marketplaceApi.updateCurrentUserSubscriptionAddress(
          this.subscriptionId,
          this.shippingAddressId
        );

        this.handleCloseModal();
        this.$emit('subscriptionUpdated');
        this.$snotify.success(t('Subscription shipping address updated'));
      } catch (e) {
        this.shippingAddressesUpdateLoading = false;
        this.$snotify.error(t('Unable to update subscription shipping address'));
      }
    },
    handleAddressSelect(addressId) {
      this.shippingAddressId = addressId;
    },
    handleCloseModal() {
      this.modalOpen = false;
      this.shippingAddressesUpdateLoading = false;
    }
  }
};
</script>
